import Markdown, { type Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

/**
 * The renderer for ASSISTANT / AI message bodies, shared by the three chat surfaces:
 * the /ai history page, the rail's AssistantPanel, and the per-property ThreadPanel.
 *
 * 🚨 WHY THIS EXISTS. Master Joseph, on his phone, got an answer that printed as raw pipes:
 *
 *     | Metric | Value |
 *     |---|---|
 *     | Properties in database | 7,747 |
 *
 * The model returns GitHub-flavoured markdown and every surface hand-rolled its own tiny
 * renderer - bold and bullets at best, `whitespace-pre-wrap` at worst - so a table was never
 * anything but text. `react-markdown` and `remark-gfm` were BOTH already in package.json with
 * ZERO importers anywhere in src: the dependency was installed and never wired. remark-gfm is
 * exactly the plugin that turns those pipes into a table, so this is a wiring job, not a
 * new capability.
 *
 * ⛔ ASSISTANT TEXT ONLY. A message the operator typed himself renders literally - his `*hello*`
 * is his words, not markup, and the callers keep user messages exactly as they were.
 *
 * 🚨 `remark-breaks` IS LOAD-BEARING, NOT A GARNISH. Plain markdown treats a single newline as a
 * space, so an answer written as consecutive lines collapses into one run-on paragraph. That is
 * not hypothetical: of the 8 assistant messages stored in PRODUCTION, 5 contain bare single
 * newlines and 4 contain pipe tables. Rendering markdown WITHOUT this plugin would therefore
 * have broken the majority of his existing history to fix the tables - a worse outcome than the
 * raw pipes it set out to fix. The /ai renderer this replaces emitted a <br> for every newline,
 * and this is what keeps that promise.
 *
 * ⭐ ORDER DOES NOT MATTER HERE, and that was MEASURED rather than assumed, because gfm tables
 * and breaks look like they should interact: the `|---|---|` delimiter row is exactly the sort of
 * line a naive break-inserter would mangle. Rendering the same table, the same line-broken text
 * and the same blank-line paragraphs under [gfm, breaks], [breaks, gfm] and [gfm] alone produced
 * BYTE-IDENTICAL html for the first two. The reason is structural: remark-gfm registers micromark
 * SYNTAX extensions that run at parse time, so a table is already a table node before
 * remark-breaks - an mdast transformer over text nodes - ever visits it. gfm is listed first for
 * reading order only.
 *
 * ⛔ NO RAW HTML, DELIBERATELY. There is no `rehype-raw` here and none may be added. This text
 * arrives from a MODEL, so it is untrusted input on the same footing as anything else crossing
 * the wire; react-markdown drops raw HTML nodes by default, and that default is the security
 * property that stops a `<script>` or an `<img onerror=...>` in an answer from becoming a real
 * element in the operator's DOM. Turning it on would be an XSS hole, not a formatting choice.
 *
 * ⚠️ NOT `AgentMarkdown`. That one styles compact agent REPORTS (Prep Call, Deal Radar) and is
 * a different genre with two callers of its own; it is left alone on purpose. Consolidating the
 * two is a later question, not this fix.
 */

/**
 * 🚨 A WIDE TABLE SCROLLS INSIDE ITSELF, IT DOES NOT BREAK THE PAGE. He reads this at 375px on
 * a phone and inside a 380px rail, and five markdown columns are wider than either. The
 * `overflow-x-auto` wrapper is what confines that overflow to the message bubble - and it also
 * zeroes the element's automatic minimum size, so a flex-item bubble around it cannot be forced
 * past its own max-width either. A horizontal-overflow defect on /ai was fixed one commit ago
 * (12701e8) and must not come back through this door.
 *
 * `min-w-max` is the other half: without it the table would obey `w-full`, squeeze every column
 * to fit and wrap "Properties in database" one word per line rather than scroll. With it the
 * table is at least as wide as its content needs and the WRAPPER does the scrolling.
 *
 * ⚠️ jsdom has no layout engine, so no test in this repo can prove the scroll happens - only
 * that the wrapper is there. The real check is scrollWidth vs clientWidth in a browser at
 * 375px; see docs/BROWSER-VERIFICATION.md.
 */
const TABLE_WRAPPER = 'my-2 overflow-x-auto';

/**
 * Borders are `black/10` and `black/5` rather than `gray-200`, because the same table has to sit
 * on three different bubble colours - gray-100 in the rail and on /ai, blue-50 for a thread
 * answer, amber-50 for a volunteered one. A translucent black reads as the same light hairline
 * on all of them; a fixed gray disappears on one and fights the tint on another. The proportions
 * (text-xs, a subtle header row, hairline row separators) follow DemographicsPanel, which is how
 * the app already draws a small table.
 */
const components: Components = {
  table: ({ node: _node, ...props }) => (
    <div className={TABLE_WRAPPER}>
      <table className="w-full min-w-max border-collapse text-xs" {...props} />
    </div>
  ),
  thead: ({ node: _node, ...props }) => (
    <thead className="border-b border-black/10 bg-black/5" {...props} />
  ),
  th: ({ node: _node, ...props }) => (
    <th className="px-2 py-1.5 text-left font-semibold align-bottom" {...props} />
  ),
  td: ({ node: _node, ...props }) => (
    <td className="px-2 py-1.5 align-top border-b border-black/5" {...props} />
  ),

  /**
   * 🚨 Links open in a new tab, and `noopener noreferrer` is not optional: the href came from a
   * model, so the destination is not ours, and without `noopener` that page gets a handle on
   * this window through `window.opener`.
   */
  a: ({ node: _node, ...props }) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      className="underline underline-offset-2 hover:opacity-80"
    />
  ),

  /**
   * The /ai code fence keeps the exact look it had before this change - dark ground, green text,
   * its own horizontal scroll. react-markdown nests a `<code>` inside the `<pre>`, and that inner
   * one must NOT pick up the inline pill below, hence the descendant overrides: react-markdown
   * passes no `inline` flag (removed in v9), so the parent element is the reliable signal, and
   * CSS can say "a code inside this pre" where a component override cannot see its own parent.
   */
  pre: ({ node: _node, ...props }) => (
    <pre
      className="bg-gray-900 text-green-300 rounded-md p-3 my-2 text-xs overflow-x-auto whitespace-pre-wrap [&_code]:bg-transparent [&_code]:text-inherit [&_code]:p-0 [&_code]:text-xs"
      {...props}
    />
  ),
  code: ({ node: _node, ...props }) => (
    <code className="bg-gray-100 text-red-600 px-1 rounded text-xs" {...props} />
  ),

  // Headings stay chat-sized. A model that opens with "## Summary" must not print a 24px banner
  // inside a 380px bubble, so every level lands on the same small, quiet treatment the agent
  // surfaces already use.
  h1: ({ node: _node, ...props }) => (
    <p className="text-sm font-semibold mt-3 mb-1 first:mt-0" {...props} />
  ),
  h2: ({ node: _node, ...props }) => (
    <p className="text-sm font-semibold mt-3 mb-1 first:mt-0" {...props} />
  ),
  h3: ({ node: _node, ...props }) => (
    <p className="text-xs font-semibold uppercase tracking-wider opacity-70 mt-3 mb-1 first:mt-0" {...props} />
  ),

  p: ({ node: _node, ...props }) => (
    <p className="my-1.5 leading-relaxed first:mt-0 last:mb-0" {...props} />
  ),
  ul: ({ node: _node, ...props }) => (
    <ul className="my-1.5 list-disc pl-5 space-y-0.5" {...props} />
  ),
  ol: ({ node: _node, ...props }) => (
    <ol className="my-1.5 list-decimal pl-5 space-y-0.5" {...props} />
  ),
  // Tailwind's preflight already keeps `strong` bold, but say it here anyway: bold is the single
  // most common thing the model emits and it should not depend on a reset staying put.
  strong: ({ node: _node, ...props }) => <strong className="font-semibold" {...props} />,
  em: ({ node: _node, ...props }) => <em className="italic" {...props} />,
  blockquote: ({ node: _node, ...props }) => (
    <blockquote className="my-2 border-l-2 border-black/10 pl-3 opacity-80" {...props} />
  ),
  hr: ({ node: _node, ...props }) => <hr className="my-3 border-black/10" {...props} />,
};

/**
 * @param text The assistant's raw markdown. Never a message the operator typed.
 */
export default function ChatMarkdown({ text }: { text: string }) {
  return (
    // min-w-0: where the bubble is a flex item, this is what lets the wrapper shrink below its
    // content instead of widening its parent.
    <div className="min-w-0 break-words">
      <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} components={components}>
        {text}
      </Markdown>
    </div>
  );
}
